import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronsLeft from "@sonic/Assets/Icons/ChevronsLeft";
import ChevronsRight from "@sonic/Assets/Icons/ChevronsRight";
import { customerState } from "@sonic/Store/GemecoState";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

export interface ProductDetailsNavigationStyles {
    container?: InjectableCss;
    links?: ButtonPresentationProps;
    linkContainer?: InjectableCss;
    compare?: ButtonPresentationProps;
    leftScrollButton?: ButtonPresentationProps;
    rightScrollButton?: ButtonPresentationProps;
}

export type mappedAnchorLink = {
    active: boolean;
    contentName: string;
    contentID: string;
    sortOrder: number;
};
interface OwnProps {
    navigationItems: mappedAnchorLink[];
}

const mapStateToProps = (state: customerState) => ({
    location: getLocation(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const active: React.CSSProperties = {
    backgroundColor: "transparent",
    borderBottom: `1px solid ${getColor("main.primary")}`,
};

const hover: React.CSSProperties = {
    backgroundColor: "transparent",
    borderBottom: `1px solid ${getColor("main.primary")}`,
};

export const viewStyles: ProductDetailsNavigationStyles = {
    container: {
        css: css`
            position: relative;
            display: flex;
            min-height: 75px;
            width: 100%;
            max-width: 1400px;
        `,
    },
    links: {},
    linkContainer: {
        css: css`
            scroll-behavior: smooth;
            padding-top: 22px;
            display: flex;
            width: 100%;
            margin: 0 auto;
            overflow-x: scroll;
            ::-webkit-scrollbar {
                visibility: visible;
            }
            -ms-overflow-style: block; /* IE and Edge */
            scrollbar-width: 100px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            gap: 30px;
                            overflow-x: auto;
                            ::-webkit-scrollbar {
                                display: none;
                            }
                            -ms-overflow-style: none; /* IE and Edge */
                            scrollbar-width: none;
                        `,
                        css``,
                        css``,
                        css`
                            overflow-x: scroll;
                            ::-webkit-scrollbar {
                                visibility: visible;
                            }
                            -ms-overflow-style: block; /* IE and Edge */
                            scrollbar-width: 100px;
                        `,
                        css`
                            overflow-x: scroll;
                            ::-webkit-scrollbar {
                                visibility: visible;
                            }
                            -ms-overflow-style: block; /* IE and Edge */
                            scrollbar-width: 100px;
                            max-width: 1400px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    leftScrollButton: {
        size: 24,
        sizeVariant: "large",
        css: css`
            background-color: #fff;
            padding: 0;
            z-index: 1;
            border: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        css`
                            left: 20px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    rightScrollButton: {
        size: 24,
        sizeVariant: "large",
        css: css`
            background-color: #fff;
            padding: 0;
            border: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        css`
                            right: 20px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    compare: {
        css: css`
            max-height: 50px;
            margin: auto 0;
        `,
        shape: "pill",
    },
};

const dotColorStyles = `
        color: #E30613;
        background-color: transparent;
        font-weight: 700;
    `;

const StyledAnchor = styled.a<ButtonPresentationProps & { index: number; selectedIndex: number }>`
    position: relative;
    height: 100%;
    white-space: nowrap;
    background: transparent;
    color: black;
    font-weight: 400;
    border: 0;
    margin: 0 auto;
    ${props => props.index === props.selectedIndex && dotColorStyles};
    &:hover {
        color: #e30613;
    }
`;

const styles = viewStyles;

const AnchorNavigation: React.FC<Props> = props => {
    const { navigationItems, location } = props;
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollPosition, setScrollPostition] = useState(0);
    const linkContainer = React.useRef<HTMLDivElement>(null);
    const [showScrollbutton, setShowScrollButton] = useState(false);

    const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

    React.useEffect(() => {
        const container = linkContainer.current;

        if (container) {
            const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
            setHasHorizontalScroll(hasHorizontalScroll);

            setShowScrollButton(hasHorizontalScroll);
        }
    }, [linkContainer.current?.scrollWidth, navigationItems]);

    const handleScrollClick = (e: React.MouseEvent, direction: string) => {
        if (!linkContainer.current) {
            return;
        }

        if (direction === "right") {
            if (linkContainer.current.scrollLeft === linkContainer.current.offsetWidth) {
                return;
            }
            linkContainer.current.scrollLeft = linkContainer.current.scrollLeft + 250;
        }
        if (direction === "left") {
            if (linkContainer.current.scrollLeft === 0) {
                return;
            }
            linkContainer.current.scrollLeft = linkContainer.current.scrollLeft - 250;
        }

        setScrollPostition(linkContainer.current?.scrollLeft);
    };

    const handleClick = (event: React.MouseEvent, index: number, link: mappedAnchorLink) => {
        event.preventDefault();
        setSelectedIndex(index);
        document.getElementById(link.contentID)?.scrollIntoView({ behavior: "smooth" });
    };

    React.useEffect(() => {
        if (!linkContainer.current) {
            return;
        }

        if (hasHorizontalScroll) {
            setShowScrollButton(true);
        } else {
            setShowScrollButton(false);
        }
    }, [linkContainer.current, hasHorizontalScroll]);

    const activeBorder = (i: number) => {
        if (i === selectedIndex) {
            return `
            color: #E30613;
            background-color: transparent;
        
            ::after {
                width: 100%;
                }
            `;
        }
        return "";
    };

    const dotButtonStyles = (i: number) => ({
        css: css`
            background: transparent;
            color: black;
            font-weight: 400;
            border: 0;
            margin: 0 auto;
            ${i === selectedIndex && dotColorStyles};
            :hover {
                color: #e30613;
            }
        `,
        activeStyle: active,
        hoverStyle: hover,
    });

    const afterEffect = (i: number) => ({
        css: css`
            display: flex;
            height: 100%;
            flex-wrap: wrap;
            align-items: center;
            position: relative;
            ::after {
                content: "";
                display: block;
                position: absolute;
                width: 0;
                bottom: 0;
                height: 3px;
                background: #e30613;
                transition: width 0.2s ease-in;
            }
            :hover::after {
                width: 100%;
            }
            :hover {
                color: #e30613;
            }
            ${activeBorder(i)}
        `,
    });

    return (
        <StyledWrapper {...styles.container}>
            {showScrollbutton && (
                <Button
                    {...styles.leftScrollButton}
                    icon={{ src: ChevronsLeft, position: "left" }}
                    onClick={e => handleScrollClick(e, "left")}
                >
                    {}
                </Button>
            )}
            <StyledWrapper ref={linkContainer} {...styles.linkContainer}>
                {navigationItems.map((link, i) => {
                    return (
                        link.active && (
                            <StyledWrapper {...afterEffect(i)} key={link.contentID}>
                                <StyledAnchor
                                    selectedIndex={selectedIndex}
                                    index={i}
                                    {...dotButtonStyles(i)}
                                    href={`${location.pathname}#${link.contentID}${location.search}`}
                                    onClick={e => handleClick(e, i, link)}
                                    key={link.contentID}
                                >
                                    {link.contentName}
                                </StyledAnchor>
                            </StyledWrapper>
                        )
                    );
                })}
            </StyledWrapper>
            {showScrollbutton && (
                <Button
                    {...styles.rightScrollButton}
                    icon={{ src: ChevronsRight, position: "right" }}
                    onClick={e => handleScrollClick(e, "right")}
                >
                    {}
                </Button>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(AnchorNavigation);
